import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search as SearchIcon, ArrowLeft, SlidersHorizontal, X, Bookmark,
  BookmarkPlus, Trash2, MapPin, Users, Sparkles, Clock
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import BottomNav from '../components/BottomNav';
import OnlineDot from '../components/OnlineDot';
import ProfileDetailModal from '../components/ProfileDetailModal';
import { useAuth } from '../contexts/AuthContext';
import {
  searchProfiles, getSavedSearches, createSavedSearch, deleteSavedSearch,
  recordProfileView, startConversation
} from '../services/api';
import { useDefaultAvatar } from '../services/defaultAvatar';
import { getPrimaryPhoto } from '../services/photoHelpers';

const EMPTY = {
  keyword: '',
  min_age: '', max_age: '',
  gender: '', interested_in: '',
  looking_for: '',
  from_country: '', from_region: '', from_city: '',
  current_country: '', current_region: '', current_city: '',
  has_children: '', wants_children: '',
  smoking: '', drinking: '',
  ethnicity: '', religion: '',
  interests: '',
  verified_only: false, with_photos: true,
  online_now: false, new_members: false,
  sort: 'active',
};

const cleanFilters = (f) => {
  const out = {};
  Object.entries(f).forEach(([k, v]) => {
    if (v === '' || v === null || v === undefined) return;
    if (Array.isArray(v) && v.length === 0) return;
    if (typeof v === 'boolean' && !v) return;
    if (k === 'interests' && typeof v === 'string') {
      const arr = v.split(',').map(s => s.trim()).filter(Boolean);
      if (arr.length) out.interests = arr;
      return;
    }
    if ((k === 'min_age' || k === 'max_age') && typeof v === 'string') {
      const n = parseInt(v, 10); if (!Number.isNaN(n)) out[k] = n; return;
    }
    out[k] = v;
  });
  return out;
};

const SearchPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const defaultAvatar = useDefaultAvatar();
  const [filters, setFilters] = useState(EMPTY);
  const [showFilters, setShowFilters] = useState(true);
  const [count, setCount] = useState(0);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [countLoading, setCountLoading] = useState(false);
  const [saved, setSaved] = useState([]);
  const [savingName, setSavingName] = useState('');
  const [showSaved, setShowSaved] = useState(false);
  const skipRef = useRef(0);
  const [hasMore, setHasMore] = useState(true);
  const [detailUserId, setDetailUserId] = useState(null);
  const [upgradePrompt, setUpgradePrompt] = useState(null);

  const set = (k, v) => setFilters(prev => ({ ...prev, [k]: v }));

  const doSearch = useCallback(async (append = false) => {
    setLoading(true);
    try {
      const skip = append ? skipRef.current : 0;
      const clean = cleanFilters(filters);
      const res = await searchProfiles(clean, { skip, limit: 24 });
      setCount(res.data.total);
      setResults(prev => append ? [...prev, ...res.data.results] : res.data.results);
      skipRef.current = skip + res.data.results.length;
      setHasMore(res.data.results.length >= 24 && skipRef.current < res.data.total);
    } catch (e) {
      console.error('Search failed', e);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  // Live count when filters change (debounced)
  useEffect(() => {
    let cancelled = false;
    const handle = setTimeout(async () => {
      setCountLoading(true);
      try {
        const clean = cleanFilters(filters);
        const res = await searchProfiles(clean, { count_only: true });
        if (!cancelled) setCount(res.data.total);
      } catch (e) {
        console.error('Count failed', e);
      } finally {
        if (!cancelled) setCountLoading(false);
      }
    }, 350);
    return () => { cancelled = true; clearTimeout(handle); };
  }, [filters]);

  useEffect(() => {
    // Initial full search
    doSearch(false);
    // Load saved searches
    getSavedSearches().then(r => setSaved(r.data)).catch(() => { /* ignore */ });
  }, []);

  const handleReset = () => setFilters(EMPTY);

  const handleSave = async () => {
    if (!savingName.trim()) return;
    try {
      const res = await createSavedSearch(savingName.trim(), cleanFilters(filters), false);
      setSaved(prev => [res.data, ...prev]);
      setSavingName('');
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to save');
    }
  };

  const handleApplySaved = (s) => {
    setFilters({ ...EMPTY, ...s.filters, interests: Array.isArray(s.filters?.interests) ? s.filters.interests.join(', ') : (s.filters?.interests || '') });
    setShowSaved(false);
    setTimeout(() => doSearch(false), 400);
  };

  const handleDeleteSaved = async (id) => {
    try {
      await deleteSavedSearch(id);
      setSaved(prev => prev.filter(s => s.search_id !== id));
    } catch { /* ignore */ }
  };

  const openProfile = (u) => {
    if (!u?.user_id) return;
    // Fire-and-forget view tracking (respects backend track_profile_views toggle)
    recordProfileView(u.user_id, 'search').catch(() => { /* ignore */ });
    setDetailUserId(u.user_id);
  };

  const handleMessage = async (profile) => {
    if (!profile?.user_id) return;
    try {
      const res = await startConversation(profile.user_id);
      setDetailUserId(null);
      navigate(`/messages/${res.data.match_id}`);
    } catch (e) {
      const status = e.response?.status;
      if (status === 402 || status === 403) {
        setDetailUserId(null);
        setUpgradePrompt({
          message: e.response.data?.detail ||
            'Premium members can message anyone. Free members must match first.'
        });
      } else {
        alert(e.response?.data?.detail || 'Failed to open conversation');
      }
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] pb-24" data-testid="search-page">
      {/* Header */}
      <div className="fixed top-0 left-0 right-0 z-30 bg-white/95 backdrop-blur-lg border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="p-2 -ml-2 hover:bg-gray-100 rounded-full" data-testid="search-back">
            <ArrowLeft className="w-5 h-5 text-gray-700" />
          </button>
          <div className="flex-1">
            <h1 className="text-xl font-bold text-gray-900" style={{ fontFamily: 'Outfit' }}>Advanced Search</h1>
            <p className="text-xs text-gray-500">
              {countLoading ? 'Counting…' : `${count.toLocaleString()} member${count === 1 ? '' : 's'} match`}
            </p>
          </div>
          <Button variant="ghost" size="sm" onClick={() => setShowSaved(v => !v)} data-testid="toggle-saved">
            <Bookmark className="w-4 h-4 mr-1" /> Saved
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setShowFilters(v => !v)} data-testid="toggle-filters">
            <SlidersHorizontal className="w-4 h-4 mr-1" /> {showFilters ? 'Hide' : 'Filters'}
          </Button>
        </div>
      </div>

      <div className="pt-20 px-4 max-w-4xl mx-auto grid lg:grid-cols-[320px,1fr] gap-6">
        {/* Filters */}
        <AnimatePresence>
          {showFilters && (
            <motion.aside
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              className="bg-white rounded-2xl p-5 shadow-sm space-y-4"
              data-testid="filters-panel"
            >
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-gray-900">Filters</h3>
                <button onClick={handleReset} className="text-xs text-gray-500 hover:text-gray-700" data-testid="reset-filters">
                  Reset
                </button>
              </div>

              <div>
                <Label className="text-xs text-gray-600">Keyword</Label>
                <div className="relative mt-1">
                  <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input value={filters.keyword} onChange={e => set('keyword', e.target.value)} placeholder="name, bio, ideal date…" className="pl-9 rounded-lg" data-testid="filter-keyword" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs text-gray-600">Min age</Label>
                  <Input type="number" min="18" max="99" value={filters.min_age} onChange={e => set('min_age', e.target.value)} className="mt-1 rounded-lg" data-testid="filter-min-age" />
                </div>
                <div>
                  <Label className="text-xs text-gray-600">Max age</Label>
                  <Input type="number" min="18" max="99" value={filters.max_age} onChange={e => set('max_age', e.target.value)} className="mt-1 rounded-lg" data-testid="filter-max-age" />
                </div>
              </div>

              <div>
                <Label className="text-xs text-gray-600">Gender</Label>
                <Select value={filters.gender || 'any'} onValueChange={v => set('gender', v === 'any' ? '' : v)}>
                  <SelectTrigger className="mt-1 rounded-lg" data-testid="filter-gender"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Any</SelectItem>
                    <SelectItem value="man">Man</SelectItem>
                    <SelectItem value="woman">Woman</SelectItem>
                    <SelectItem value="non-binary">Non-binary</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-xs text-gray-600">Looking for</Label>
                <Select value={filters.looking_for || 'any'} onValueChange={v => set('looking_for', v === 'any' ? '' : v)}>
                  <SelectTrigger className="mt-1 rounded-lg" data-testid="filter-looking-for"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Any</SelectItem>
                    <SelectItem value="long-term">Long-term partner</SelectItem>
                    <SelectItem value="short-term">Short-term</SelectItem>
                    <SelectItem value="friendship">Friendship</SelectItem>
                    <SelectItem value="marriage">Marriage</SelectItem>
                    <SelectItem value="casual">Casual</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs text-gray-600">Country</Label>
                  <Input value={filters.current_country} onChange={e => set('current_country', e.target.value)} placeholder="e.g. Guyana" className="mt-1 rounded-lg" data-testid="filter-current-country" />
                </div>
                <div>
                  <Label className="text-xs text-gray-600">City</Label>
                  <Input value={filters.current_city} onChange={e => set('current_city', e.target.value)} placeholder="e.g. Georgetown" className="mt-1 rounded-lg" data-testid="filter-current-city" />
                </div>
              </div>
              <div>
                <Label className="text-xs text-gray-600">Region</Label>
                <Input value={filters.current_region} onChange={e => set('current_region', e.target.value)} placeholder="e.g. Region 4" className="mt-1 rounded-lg" data-testid="filter-current-region" />
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs text-gray-600">Ethnicity</Label>
                  <Input value={filters.ethnicity} onChange={e => set('ethnicity', e.target.value)} placeholder="e.g. Indo-Guyanese" className="mt-1 rounded-lg" data-testid="filter-ethnicity" />
                </div>
                <div>
                  <Label className="text-xs text-gray-600">Religion</Label>
                  <Input value={filters.religion} onChange={e => set('religion', e.target.value)} placeholder="e.g. Hindu" className="mt-1 rounded-lg" data-testid="filter-religion" />
                </div>
              </div>

              <div>
                <Label className="text-xs text-gray-600">Interests (comma-separated)</Label>
                <Input value={filters.interests} onChange={e => set('interests', e.target.value)} placeholder="music, hiking" className="mt-1 rounded-lg" data-testid="filter-interests" />
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs text-gray-600">Smoking</Label>
                  <Select value={filters.smoking || 'any'} onValueChange={v => set('smoking', v === 'any' ? '' : v)}>
                    <SelectTrigger className="mt-1 rounded-lg" data-testid="filter-smoking"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="any">Any</SelectItem>
                      <SelectItem value="no">No</SelectItem>
                      <SelectItem value="sometimes">Sometimes</SelectItem>
                      <SelectItem value="yes">Yes</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-gray-600">Drinking</Label>
                  <Select value={filters.drinking || 'any'} onValueChange={v => set('drinking', v === 'any' ? '' : v)}>
                    <SelectTrigger className="mt-1 rounded-lg" data-testid="filter-drinking"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="any">Any</SelectItem>
                      <SelectItem value="no">No</SelectItem>
                      <SelectItem value="sometimes">Sometimes</SelectItem>
                      <SelectItem value="yes">Yes</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-3 pt-2 border-t border-gray-100">
                <label className="flex items-center justify-between text-sm">
                  <span className="text-gray-700 flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-emerald-500" /> Online now</span>
                  <Switch checked={filters.online_now} onCheckedChange={v => set('online_now', v)} data-testid="filter-online-now" />
                </label>
                <label className="flex items-center justify-between text-sm">
                  <span className="text-gray-700 flex items-center gap-2"><Sparkles className="w-3.5 h-3.5 text-[#FFCE00]" /> New members</span>
                  <Switch checked={filters.new_members} onCheckedChange={v => set('new_members', v)} data-testid="filter-new-members" />
                </label>
                <label className="flex items-center justify-between text-sm">
                  <span className="text-gray-700">Verified only</span>
                  <Switch checked={filters.verified_only} onCheckedChange={v => set('verified_only', v)} data-testid="filter-verified" />
                </label>
                <label className="flex items-center justify-between text-sm">
                  <span className="text-gray-700">Has photos</span>
                  <Switch checked={filters.with_photos} onCheckedChange={v => set('with_photos', v)} data-testid="filter-with-photos" />
                </label>
              </div>

              <div>
                <Label className="text-xs text-gray-600">Sort by</Label>
                <Select value={filters.sort} onValueChange={v => set('sort', v)}>
                  <SelectTrigger className="mt-1 rounded-lg" data-testid="filter-sort"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Recently active</SelectItem>
                    <SelectItem value="newest">Newest members</SelectItem>
                    <SelectItem value="age_asc">Age (young → old)</SelectItem>
                    <SelectItem value="age_desc">Age (old → young)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Button onClick={() => doSearch(false)} className="w-full bg-[#009E60] hover:bg-[#007A4B] rounded-full" data-testid="run-search">
                <SearchIcon className="w-4 h-4 mr-2" /> Search {count.toLocaleString()}
              </Button>

              <div className="pt-2 border-t border-gray-100 space-y-2">
                <Label className="text-xs text-gray-600">Save this search</Label>
                <div className="flex gap-2">
                  <Input value={savingName} onChange={e => setSavingName(e.target.value)} placeholder="e.g. Georgetown 25-35" className="rounded-lg" data-testid="save-name" />
                  <Button variant="outline" onClick={handleSave} disabled={!savingName.trim()} data-testid="save-search">
                    <BookmarkPlus className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </motion.aside>
          )}
        </AnimatePresence>

        {/* Results */}
        <section>
          {/* Saved searches panel */}
          <AnimatePresence>
            {showSaved && (
              <motion.div
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                className="bg-white rounded-2xl p-4 shadow-sm mb-4"
                data-testid="saved-searches-panel"
              >
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-gray-900 text-sm">Saved searches ({saved.length})</h3>
                  <button onClick={() => setShowSaved(false)} className="p-1"><X className="w-4 h-4 text-gray-500" /></button>
                </div>
                {saved.length === 0 ? (
                  <p className="text-sm text-gray-500">No saved searches yet. Fill in filters and hit “Save”.</p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {saved.map(s => (
                      <div key={s.search_id} className="group inline-flex items-center gap-2 bg-gray-50 hover:bg-gray-100 rounded-full pl-3 pr-1 py-1" data-testid={`saved-item-${s.search_id}`}>
                        <button onClick={() => handleApplySaved(s)} className="text-sm text-gray-800">
                          {s.name}
                        </button>
                        <button onClick={() => handleDeleteSaved(s.search_id)} className="p-1 rounded-full hover:bg-red-100 opacity-60 group-hover:opacity-100" data-testid={`delete-saved-${s.search_id}`}>
                          <Trash2 className="w-3.5 h-3.5 text-red-500" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Result summary */}
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm text-gray-600">
              {loading ? 'Searching…' : `Showing ${results.length} of ${count.toLocaleString()}`}
            </p>
          </div>

          {/* Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="search-results">
            {results.map(p => (
              <button
                key={p.user_id}
                onClick={() => openProfile(p)}
                className="relative group rounded-2xl overflow-hidden bg-white shadow-sm hover:shadow-md transition-shadow text-left"
                data-testid={`result-${p.user_id}`}
              >
                <div className="aspect-[3/4] w-full bg-gray-100 overflow-hidden">
                  <img
                    src={getPrimaryPhoto(p, defaultAvatar) || defaultAvatar}
                    alt={p.display_name || p.name}
                    className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform"
                    loading="lazy"
                  />
                </div>
                <div className="absolute top-2 left-2 flex items-center gap-1.5">
                  {p.is_online && (
                    <span className="inline-flex items-center gap-1 bg-emerald-500 text-white text-[10px] font-medium px-2 py-0.5 rounded-full">
                      <span className="w-1.5 h-1.5 rounded-full bg-white" /> Online
                    </span>
                  )}
                  {p.is_verified && (
                    <span className="bg-blue-500 text-white text-[10px] font-medium px-2 py-0.5 rounded-full">Verified</span>
                  )}
                </div>
                <div className="p-2.5">
                  <div className="text-sm font-semibold text-gray-900 truncate">
                    {p.display_name || p.name}{p.age ? `, ${p.age}` : ''}
                  </div>
                  <div className="flex items-center gap-1 text-xs text-gray-500 mt-0.5 truncate">
                    <MapPin className="w-3 h-3" />
                    <span className="truncate">{p.current_city || p.location || '—'}</span>
                  </div>
                  {!p.is_online && (
                    <OnlineDot isOnline={false} lastActive={p.last_active} size={6} className="mt-1" />
                  )}
                </div>
              </button>
            ))}
          </div>

          {results.length === 0 && !loading && (
            <div className="text-center py-16 bg-white rounded-2xl">
              <Users className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <h3 className="font-semibold text-gray-900 mb-1">No matches yet</h3>
              <p className="text-sm text-gray-500">Try widening your filters.</p>
            </div>
          )}

          {hasMore && !loading && results.length > 0 && (
            <div className="text-center mt-4">
              <Button variant="outline" onClick={() => doSearch(true)} data-testid="load-more">Load more</Button>
            </div>
          )}
        </section>
      </div>

      {detailUserId && (
        <ProfileDetailModal
          userId={detailUserId}
          onClose={() => setDetailUserId(null)}
          onMessage={(p) => handleMessage(p)}
          onBlocked={() => {
            const removed = detailUserId;
            setDetailUserId(null);
            setResults(prev => prev.filter(r => r.user_id !== removed));
          }}
          onReported={() => setDetailUserId(null)}
        />
      )}

      <AnimatePresence>
        {upgradePrompt && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4"
            onClick={() => setUpgradePrompt(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-3xl p-6 max-w-sm w-full text-center"
              data-testid="upgrade-prompt"
            >
              <h3 className="text-xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Outfit' }}>
                Message anyone with Premium
              </h3>
              <p className="text-gray-600 mb-5 text-sm">{upgradePrompt.message}</p>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setUpgradePrompt(null)} className="flex-1 rounded-full">Maybe later</Button>
                <Button
                  onClick={() => { setUpgradePrompt(null); navigate('/pricing'); }}
                  className="flex-1 bg-[#FFCE00] hover:bg-yellow-500 text-gray-900 rounded-full font-semibold"
                >
                  Go Premium
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <BottomNav />
    </div>
  );
};

export default SearchPage;
